import type { CertificationScheme } from '@safc/configuration';
import type { issueFields } from '@safc/configuration';
import {
  CertificationSchemeCorsia,
  CertificationType,
} from '@safc/configuration';
import { FormInput } from 'components/ui/form/FormInput/FormInput.js';
import { InputAdornmentText } from 'components/ui/form/Input/Input.styled.js';
import type { Control } from 'react-hook-form';

import { FormHelp } from '../components/IssueForm.styled.js';
import { IssueFormRow } from '../components/IssueFormRow.js';
import { IssueFormYesNo } from '../components/IssueFormYesNo.js';

export const ghgDependenciesFields = {
  isSolidWaste: 'product.feedStock.isSolidWaste',
  certificationScheme: 'certification.scheme',
  certificationType: 'certification.type',
} as const;

type Props = {
  control: Control<any>;
  isSolidWaste: boolean | undefined;
  certificationType: CertificationType | undefined;
  certificationScheme: CertificationScheme | undefined;
  totalLCAValueincludeEmissionsFromDeliveryToAirport: boolean | undefined;
  useDefaultValueForDeliveryToAirport: boolean | undefined;
};

export const IssueFormGreenhouseGas = ({ control, certificationType, isSolidWaste, certificationScheme, totalLCAValueincludeEmissionsFromDeliveryToAirport, useDefaultValueForDeliveryToAirport }: Props) => {
  if (certificationType === undefined || certificationScheme === undefined || isSolidWaste === undefined) {
    return <FormHelp>Fill certification and product tabs first</FormHelp>;
  }

  const isCorsia = CertificationSchemeCorsia.includes(certificationScheme);
  const help = helpText[certificationType][isCorsia ? 'corsia' : 'eu'];

  const schemaTypeFields = (() => {
    if (certificationType === CertificationType.Actual) {
      if (isCorsia) {
        return <CorsiaActual control={control} />;
      } else {
        return <EuActual control={control} />;
      }
    } else {
      if (isCorsia) {
        return <CorsiaDefault control={control} />;
      } else {
        return <EuDefault control={control} />;
      }
    }
  })();

  return (
    <>
      <FormHelp>{help}</FormHelp>
      {schemaTypeFields}
      {isSolidWaste && <NumberField control={control} field="greenhouseGas.recyclingEmissionsCredits" />}
      {isSolidWaste && <NumberField control={control} field="greenhouseGas.landfillEmissionsCredits" />}
      <IssueFormRow control={control} field="greenhouseGas.totalLCAValueincludeEmissionsFromDeliveryToAirport">
        {props => (<IssueFormYesNo {...props} />)}
      </IssueFormRow>
      {totalLCAValueincludeEmissionsFromDeliveryToAirport === false && (
        <IssueFormRow control={control} field="greenhouseGas.useDefaultValueForDeliveryToAirport">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      {totalLCAValueincludeEmissionsFromDeliveryToAirport === false && useDefaultValueForDeliveryToAirport === false && (
        <NumberField control={control} field="greenhouseGas.actualGHGIntensityAirportDelivery" />
      )}
    </>
  );
};

const NumberField = ({ control, field }: { control: Control<any>; field: keyof typeof issueFields }) => {
  return (
    <IssueFormRow control={control} field={field}>
      {props => (
        <FormInput
          control={props.control}
          field={{
            ...props.field,
            type: 'number',
            endAdornment: <InputAdornmentText>gCO2e/MJ</InputAdornmentText>,
          }}
        />
      )}
    </IssueFormRow>
  );
};

const CorsiaDefault = ({ control }: { control: Control<any> }) => {
  return (
    <>
      <NumberField control={control} field="greenhouseGas.totalLCAValueCorsiaDefault" />
      <NumberField control={control} field="greenhouseGas.coreLCAValue" />
      <NumberField control={control} field="greenhouseGas.inducedLandUseChange" />
      <NumberField control={control} field="greenhouseGas.ghgDisplacementEmissions" />
      <NumberField control={control} field="greenhouseGas.emissionsSavingsFromCSS" />
    </>
  );
};

const CorsiaActual = ({ control }: { control: Control<any> }) => {
  return (
    <>
      <NumberField control={control} field="greenhouseGas.totalLCAValueCorsiaActual" />
      <NumberField control={control} field="greenhouseGas.inducedLandUseChange" />
      <NumberField control={control} field="greenhouseGas.production" />
      <NumberField control={control} field="greenhouseGas.conditioning" />
      <NumberField control={control} field="greenhouseGas.processing" />
      <NumberField control={control} field="greenhouseGas.transportation" />
      <NumberField control={control} field="greenhouseGas.conversionProcesses" />
      <NumberField control={control} field="greenhouseGas.fuelTransportation" />
      <NumberField control={control} field="greenhouseGas.combustion" />
      <NumberField control={control} field="greenhouseGas.ghgDisplacementEmissions" />
      <NumberField control={control} field="greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration" />
      <NumberField control={control} field="greenhouseGas.emissionsSavingsFromCSS" />
    </>
  );
};

const EuDefault = ({ control }: { control: Control<any> }) => {
  return (
    <>
      <NumberField control={control} field="greenhouseGas.totalLCAValueEuDefault" />
      <NumberField control={control} field="greenhouseGas.inducedLandUseChange" />
      <NumberField control={control} field="greenhouseGas.ghgDisplacementEmissions" />
      <NumberField control={control} field="greenhouseGas.savingsFromCarbonAndStorage" />
    </>
  );
};

const EuActual = ({ control }: { control: Control<any> }) => {
  return (
    <>
      <NumberField control={control} field="greenhouseGas.totalLCAValueEuActual" />
      <NumberField control={control} field="greenhouseGas.inducedLandUseChange" />
      <NumberField control={control} field="greenhouseGas.displacementEmissions" />
      <NumberField control={control} field="greenhouseGas.extractionEmissions" />
      <NumberField control={control} field="greenhouseGas.annualisedEmissions" />
      <NumberField control={control} field="greenhouseGas.processingEmissions" />
      <NumberField control={control} field="greenhouseGas.transportEmissions" />
      <NumberField control={control} field="greenhouseGas.inUseFuelEmissions" />
      <NumberField control={control} field="greenhouseGas.savingsFromSoil" />
      <NumberField control={control} field="greenhouseGas.savingsFromCarbonAndStorage" />
      <NumberField control={control} field="greenhouseGas.savingsFromCarbonAndReplacement" />
      <NumberField control={control} field="greenhouseGas.savingsFromExcess" />
    </>
  );
};

const helpText = {
  [CertificationType.Actual]: {
    corsia: 'How the registry calculates final LCA value for CORSIA-certified SAF using actual LCA values: \n • \'SABA advanced\' designation = c + d + e + f + g + h + i + j + m + p + q \n • \'SABA eligible\' designation = c + d + e + f + g + h + i + j + m + q \n• \'SCS eligible\' designation = c + d + e + f + g + h + i + j + m - n - o - r',
    eu: 'How the registry calculates final LCA value for SAF certified to EU RED, ISCC PLUS, or RSB Global using actual LCA values: \n • \'SABA advanced\' designation = c + d + t + f + g + j + m + p - r - s - u \n• \'SABA eligible\' designation = c + d + t + f + g + j + m - r - s - u \n• \'SCS eligible\' designation = d + t + f + g + j + m - q - r - s - u',
  },
  [CertificationType.Default]: {
    corsia: 'How the registry calculates final LCA value for CORSIA-certified SAF using default LCA values: \n • \'SABA advanced\' designation = b + c + m + p \n • \'SABA eligible\' designation = b + c + m \n• \'SCS eligible\' designation = b + c + m - n - o - r',
    eu: 'How the registry calculates final LCA value for SAF certified to EU RED, ISCC PLUS, or RSB Global using default LCA values: \n• \'SABA advanced\' designation = a + c + m + p \n• \'SABA eligible\' designation = a + c + m \n • \'SCS eligible\' designation = a + m - r',
  },
};
